import Link from "next/link";
import { NextRouter, useRouter } from "next/router";
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import { LeftArrow, RightArrow } from "./horizontal-scroll-arrows";
import { ReactNode, useEffect, useRef } from "react";

type scrollVisibilityApiType = React.ContextType<typeof VisibilityContext>;

interface Topic {
  name: JSX.Element | string;
  key: string;
  emoji?: string;
  bgActiveClass?: string;
  seoTitle: string;
  seoDescription: string;
}
interface IExploreTopicsProps {
  topics: Topic[];
  hasSearchedData?: boolean;
  scrollToTopicsBar?: boolean;
}

interface ITopicsTabProps {
  topicKey: string;
  children?: ReactNode;
  router: NextRouter;
  textActiveClass?: string;
  textInactiveClass?: string;
  bgActiveClass?: string;
  bgInactiveClass?: string;
  active: boolean;
  hasSearchedData: boolean;
}

interface ITopicTabBaseProps {
  children?: ReactNode;
  textActiveClass?: string;
  textInactiveClass?: string;
  bgActiveClass?: string;
  bgInactiveClass?: string;
  active: boolean;
  hasSearchedData: boolean;
}

const ExploreTopics: React.FunctionComponent<IExploreTopicsProps> = ({
  topics,
  hasSearchedData,
  scrollToTopicsBar = true,
}) => {
  const router = useRouter();
  const {
    query: { topic },
  } = router;
  const scrollMenuApiRef = useRef({} as scrollVisibilityApiType);

  useEffect(() => {
    if (scrollToTopicsBar) {
      // Added an abitrary div to scroll to view when ExploreTopics is in sticky position
      setTimeout(() => {
        if (topic && scrollMenuApiRef.current) {
          // Added an abitrary div to scroll to view when ExploreTopics is in sticky position
          const el = document.getElementById("sticky-topic-header");
          el.scrollIntoView(true);
          scrollMenuApiRef.current.scrollToItem(
            scrollMenuApiRef.current.getItemElementById(topic as string),
            "smooth",
            "center"
          );
        }
      }, 100);
    }
  }, [topic, scrollMenuApiRef, scrollToTopicsBar]);

  return (
    <ScrollMenu
      LeftArrow={LeftArrow}
      RightArrow={RightArrow}
      apiRef={scrollMenuApiRef}
      transitionDuration={500}
      transitionBehavior={"smooth"}
      itemClassName="!ml-1 flex"
      scrollContainerClassName={"pt-4 pb-6 -ml-4"}
    >
      {topics.map(({ name, key, emoji, bgActiveClass = undefined }) => (
        <TopicTab
          topicKey={key}
          key={key}
          router={router}
          active={topic ? key === topic : key === topics[0].key}
          bgActiveClass={bgActiveClass}
          hasSearchedData={hasSearchedData}
        >
          {name} {emoji}
        </TopicTab>
      ))}
    </ScrollMenu>
  );
};

/// TopicTabBase with explore topics <Link> tag wrapper
const TopicTab: React.FC<ITopicsTabProps> = ({
  topicKey,
  children,
  router,
  textActiveClass,
  textInactiveClass,
  bgActiveClass,
  bgInactiveClass,
  active,
  hasSearchedData,
}) => {
  return (
    <Link
      replace
      scroll={false}
      href={{
        pathname: router.pathname,
        query: { ...router.query, topic: topicKey },
      }}
      passHref
    >
      <a className="mr-3" id={topicKey}>
        <TopicTabBase
          textActiveClass={textActiveClass}
          textInactiveClass={textInactiveClass}
          bgActiveClass={bgActiveClass}
          bgInactiveClass={bgInactiveClass}
          active={active}
          hasSearchedData={hasSearchedData}
        >
          {children}
        </TopicTabBase>
      </a>
    </Link>
  );
};

/// Base topic pill UI for reusability purposes
const TopicTabBase: React.FC<ITopicTabBaseProps> = ({
  children,
  textActiveClass = "text-white",
  textInactiveClass = "text-primary2 transition-color duration-300 hover:text-primary3",
  bgActiveClass = "bg-primary",
  bgInactiveClass = "bg-secondary transition-color duration-300 hover:bg-light-background",
  active,
  hasSearchedData,
}) => {
  return (
    <div
      className={`rounded-2xl ${
        hasSearchedData
          ? bgInactiveClass
          : active
          ? bgActiveClass
          : bgInactiveClass
      } px-4 py-2`}
    >
      <span
        className={`whitespace-nowrap font-sen ${
          hasSearchedData
            ? textInactiveClass
            : active
            ? textActiveClass
            : textInactiveClass
        }`}
      >
        {children}
      </span>
    </div>
  );
};

export default ExploreTopics;

export { TopicTabBase };
